// Display formatting shared by every page. Owner: WP04. Times are UTC instants from the API; display uses the
// profile timezone (preferences.timezone) or the browser zone (EPG02).

export const browserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/** Profile timezone when valid, else the browser zone. */
export function resolveTimeZone(pref: string | null | undefined): string {
  if (!pref) return browserTimeZone();
  try { new Intl.DateTimeFormat('en', { timeZone: pref }); return pref; } catch { return browserTimeZone(); }
}

const toDate = (t: string | number | Date) => (t instanceof Date ? t : new Date(t));

/** "8:12 PM" (locale-aware). */
export function formatTime(t: string | number | Date, timeZone?: string): string {
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone }).format(toDate(t));
}

/** "8:00 – 9:00 PM" style range. */
export function formatTimeRange(start: string | number | Date, end: string | number | Date, timeZone?: string): string {
  return `${formatTime(start, timeZone)} – ${formatTime(end, timeZone)}`;
}

/** Calendar key (YYYY-MM-DD) of an instant in a zone. */
export function dayKey(t: string | number | Date, timeZone?: string): string {
  const p = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone }).format(toDate(t));
  return p; // en-CA formats as YYYY-MM-DD
}

/** "Today", "Tomorrow", "Yesterday" or "Sat 27 Sep". */
export function formatDay(t: string | number | Date, timeZone?: string, now: Date = new Date()): string {
  const k = dayKey(t, timeZone);
  const offsets: Record<string, string> = {
    [dayKey(now, timeZone)]: 'Today',
    [dayKey(now.getTime() + 86_400_000, timeZone)]: 'Tomorrow',
    [dayKey(now.getTime() - 86_400_000, timeZone)]: 'Yesterday',
  };
  return offsets[k] ?? new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone }).format(toDate(t));
}

/** "Today, 8:12 PM" / "Sat 27 Sep, 8:12 PM". */
export function formatWhen(t: string | number | Date, timeZone?: string, now?: Date): string {
  return `${formatDay(t, timeZone, now)}, ${formatTime(t, timeZone)}`;
}

/** Runtime: "1h 48m", "48 min", "under 1 min". */
export function formatDuration(sec: number | null | undefined): string {
  if (sec === null || sec === undefined || !Number.isFinite(sec)) return '';
  const m = Math.round(sec / 60);
  if (m < 1) return 'under 1 min';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
}

/** "23 min left" given seconds remaining. */
export function formatLeft(secLeft: number): string {
  const m = Math.max(1, Math.round(secLeft / 60));
  return m < 60 ? `${m} min left` : `${formatDuration(secLeft)} left`;
}

/** Episode code "S2 E4" (specials: "Special 3"). */
export function episodeCode(season: number, number: number | null): string {
  if (season === 0) return number === null ? 'Special' : `Special ${number}`;
  return number === null ? `S${season}` : `S${season} E${number}`;
}

/** Fraction of an interval elapsed at `now` (0..1). */
export function elapsed(start: string, end: string, now: number = Date.now()): number {
  const s = Date.parse(start), e = Date.parse(end);
  return e > s ? Math.min(1, Math.max(0, (now - s) / (e - s))) : 0;
}
